import { CalendarMonth } from "@mui/icons-material";
import { Badge, Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { AppFeatureLookup } from "../../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage } from "../../App/Constants/CrmLiteConstants";
import { useGetGroupScheduledMessages } from "../../App/Queries/MessageQueries";
import { useFeaturePermission } from "../../App/State/Hooks/abacHooks";
import { IGroupMessageViewResponse } from "../../App/Typings/MessageText";
import { ActionConfirmationDialog } from "./ActionConfirmationDialog";
import { MessageTable } from "./MessageTable";
import ScheduledMessageDialogState from "./State/ScheduleMessageDialogState";
import Modal from "../../App/Components/Modal/Modal";
import TabGroup from "../../App/Components/TabGroup/TabGroup";
import { ITabGroupConfig } from "../../App/Typings/TabPanel";
import { useSearchParams } from "react-router-dom";
import ScheduleMessagesCollectionsTable from "./ScheduledMessageCollectionsTable";

const collectionTabLabel = "Collections";
const individualTabLabel = "Individual";

export default function ScheduledMessageDialog({
  isGroup,
  groupId,
  groupName,
  disableButton,
  countShow,
}: {
  isGroup: boolean;
  groupId: string;
  groupName: string;
  disableButton: boolean;
  countShow: number;
}) {
  const state = ScheduledMessageDialogState();
  const [search, setSearch] = useSearchParams();

  const { data: groupMessageList = {} as IGroupMessageViewResponse, isLoading } =
    useGetGroupScheduledMessages(groupId, isGroup);

  const handleGetScheduledMessagesClick = () => {
    state.setters.setScheduledMessages(groupMessageList.groupScheduledMessages);
    state.setters.setShowScheduledMessageDialog(true);
  };

  const [currentTab, setCurrentTab] = React.useState<"collection" | "individual" | null>(
    (search.get("tab") as "collection" | "individual" | null) || "collection",
  );
  const [tabLabel, setCurrentTabLabel] = React.useState(
    currentTab === "collection" ? collectionTabLabel : individualTabLabel,
  );

  function handleTabChange(newTab: "collection" | "individual") {
    setCurrentTabLabel(newTab === "collection" ? collectionTabLabel : individualTabLabel);
    setCurrentTab(newTab);
    setSearch({ tab: newTab });
  }

  const tabGroupConfig: ITabGroupConfig = {
    groupLabel: "Scheduled Messages",
    id: "scheduled-messages-tabs",
    currentTabState: tabLabel,
    fullWidth: false,
    changeTabState: (label) => {
      const tab = label === collectionTabLabel ? "collection" : "individual";
      handleTabChange(tab);
    },
    tabs: [
      {
        label: collectionTabLabel,
        TabComponent: <ScheduleMessagesCollectionsTable groupId={groupId} />,
      },
      {
        label: individualTabLabel,
        TabComponent: (
          <Stack direction={"column"} spacing={2}>
            <MessageTable
              id={"scheduled-message-dialog-tables-1"}
              loading={isLoading}
              columns={state.getters.columnNames}
              tableData={state.getters.schMessages}
              isEditingGroup={false}
              deleteSelectedScheduledMessages={state.handlers.deleteSelectedScheduledMessages}
              showActions={true}
              disableDeleteButton={state.getters.deleteScheduledMessageIsLoading}
            />
            <Box>
              <Typography data-test="addcandidates-currently-selected" sx={{ textAlign: "right" }}>
                {"Currently scheduled: "}
                <strong>{groupMessageList.groupScheduledMessages?.length ?? 0}</strong>
              </Typography>
            </Box>
          </Stack>
        ),
      },
    ],
  };

  return (
    <Box sx={{ position: "relative" }}>
      <ViewScheduledMessagesButton
        onClick={handleGetScheduledMessagesClick}
        disabled={disableButton}
        countShow={countShow}
      />
      <Modal
        title={`Scheduled Messages: ${groupName}`}
        content={
          <>
            <TabGroup config={tabGroupConfig} />
          </>
        }
        dataTestId="addtogroup-dialog"
        size="lg"
        isOpen={state.getters.showScheduledMessageDialog}
        handleCloseModal={(_ev) => {
          state.handlers.handleClose();
          handleTabChange("collection");
        }}
        footerContent={<></>}
      />
      <ActionConfirmationDialog
        handleClose={state.handlers.handleConfirmationResponse}
        open={state.getters.showConfirmDialog}
        message={state.getters.actionMessage}
      />
    </Box>
  );
}

const ViewScheduledMessagesButton = ({
  onClick,
  disabled,
  countShow,
}: {
  onClick: () => void;
  disabled: boolean;
  countShow: number;
}) => {
  const accessGroupScheduledMessages = useFeaturePermission(AppFeatureLookup.GroupViewScheduledMessages);
  let tooltip = "View Message History";
  if (!accessGroupScheduledMessages) {
    tooltip = crmLiteUpgradeMessage;
  } else if (disabled) {
    tooltip = "No scheduled messages available";
  }
  return (
    <Tooltip title={tooltip} arrow>
      <Box>
        <IconButton
          disabled={!accessGroupScheduledMessages || disabled}
          onClick={onClick}
          data-test="scheduled-messages-button"
        >
          <Badge badgeContent={accessGroupScheduledMessages ? countShow : undefined} max={999} color="info">
            <CalendarMonth />
          </Badge>
        </IconButton>
      </Box>
    </Tooltip>
  );
};

export const MemoScheduledMessageDialog = React.memo(ScheduledMessageDialog);
